import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";
import { Can } from "@/auth/permissions";
import { Pill, LoadingState } from "@/shared/ui/PageHeader";
import { openApiAdmin, type OpenApiKeyRow } from "@/services/openApi";
import { PartnerOpenApiOps } from "./PartnerOpenApiOps";

/** 同行详情「开放 API」标签（样稿 ⑩）：只看单个同行的接入情况——申请信息、密钥与上线、近 30 天调用。审批工作入口在「开放平台审批」页。 */
const STAGE_PILL = {
  none: { tone: "muted", label: "未开通" },
  testing: { tone: "warning", label: "测试中" },
  go_live_requested: { tone: "info", label: "上线审核中" },
  live: { tone: "success", label: "已上线" },
} as const;
const ago = (iso: string | null) => {
  if (!iso) return "—";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "刚刚" : m < 60 ? `${m} 分钟前` : m < 1440 ? `${Math.round(m / 60)} 小时前` : `${Math.round(m / 1440)} 天前`;
};

export function PartnerOpenApiTab({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["open-api", "partner", partnerId], queryFn: () => openApiAdmin.partner(partnerId) });
  const stats = useQuery({ queryKey: ["open-api", "partner-stats", partnerId], queryFn: () => openApiAdmin.stats(partnerId, 30) });
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();
  const [err, setErr] = useState<string | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [reissueAsk, setReissueAsk] = useState(false);
  const [reissued, setReissued] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["open-api"] });

  const issue = useMutation({
    mutationFn: () => openApiAdmin.issueLiveKey(partnerId),
    onSuccess: (r) => { setIssued(`${r.prefix}…${r.last4}`); refresh(); },
    onError: (e) => setErr((e as Error).message || "发正式密钥失败"),
  });

  const reissue = useMutation({
    mutationFn: () => openApiAdmin.reissueTestKey(partnerId),
    onSuccess: (r) => { setReissueAsk(false); setReissued(`${r.prefix}…${r.last4}`); setErr(null); refresh(); },
    onError: (e) => { setReissueAsk(false); setErr((e as Error).message || "重发测试密钥失败"); },
  });
  async function rejectGoLive() {
    setErr(null);
    try {
      const r = await askReason({ scene: "openapi_go_live_reject", title: "退回上线申请", subtitle: "对方回到「测试中」，原因会显示在对方门户并邮件通知；上线检查「已通过」保持不变", required: true, danger: true, confirmText: "确认退回" });
      if (!r) return;
      await openApiAdmin.rejectGoLive(partnerId, r);
      refresh();
    } catch (e) { if (!isReasonCancelled(e)) setErr((e as Error).message || "退回失败"); }
  }

  async function revoke(k: OpenApiKeyRow) {
    setErr(null);
    try {
      const r = await askReason({ scene: "openapi_revoke", title: "吊销密钥", subtitle: `${k.env === "test" ? "测试" : "正式"}密钥 ${k.prefix}…${k.last4}：立即生效，对方再调接口返回 401`, required: true, danger: true, confirmText: "确认吊销" });
      if (!r) return;
      await openApiAdmin.revokeKey(partnerId, k.id, r);
      refresh();
    } catch (e) { if (!isReasonCancelled(e)) setErr((e as Error).message || "吊销失败"); }
  }

  if (q.isLoading) return <LoadingState />;
  // 查询失败不能落到「未开通」空状态（FIX-2 第 17 条）
  if (q.isError) return (
    <div role="alert" data-testid="oa-tab-error" className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
      <span>开放 API 信息加载失败，请重试。</span>
      <button type="button" onClick={() => q.refetch()} data-testid="oa-tab-retry" className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-[12px] font-medium text-rose-700 hover:bg-rose-100">重试</button>
    </div>
  );
  const d = q.data;
  if (!d || d.stage === "none") return <div className="rounded-xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500" data-testid="oa-tab-none">这个同行还没开通开放 API。对方在同行站点「开放平台」或在开放平台官网提交申请后，会出现在「开放平台审批」。</div>;
  const st = STAGE_PILL[d.stage];
  const a = d.application;
  const allDone = d.checklist_done >= d.checklist_total;
  const liveKey = d.keys.find((k) => k.env === "live" && k.status === "active");

  return (
    <div className="flex flex-col gap-4" data-testid="oa-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-slate-900">接入申请 <Pill tone={st.tone}>开放 API · {st.label}</Pill></div>
        {a ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-slate-600 lg:grid-cols-3">
            <span>公司网站：<b>{a.website}</b></span><span>所在地：<b>{a.country}</b></span><span>预计月单量：<b>{a.monthly_volume ?? "—"}</b></span>
            <span>希望结算：<b>{a.settlement_wish ?? "—"}</b></span><span>技术联系人：<b>{a.tech_email}</b></span>
            <span>内容条款：<b>{a.terms_version} · {a.terms_accepted_at ? `已同意 ${a.terms_accepted_at.slice(0, 10)}` : "已同意"}</b></span>
          </div>
        ) : <div className="text-[13px] text-slate-500">老同行直接从同行站开通，没有申请表。</div>}
        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-slate-600 lg:grid-cols-3" data-testid="oa-facts">
          <span>技术联系人：<b data-testid="oa-fact-tech">{d.tech_email ?? "—"}</b></span>
          <span data-testid="oa-fact-passed">上线检查：<b>{d.checklist_passed_at ? `已于 ${new Date(d.checklist_passed_at).toLocaleString("zh-CN", { hour12: false })} 全部通过` : `${d.checklist_done} / ${d.checklist_total}，还没全部通过`}</b></span>
          <span>申请上线：<b>{d.go_live_requested_at ? new Date(d.go_live_requested_at).toLocaleString("zh-CN", { hour12: false }) : "—"}</b></span>
        </div>
        {d.go_live_rejection && <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="oa-fact-rejected">上线申请曾于 {new Date(d.go_live_rejection.at).toLocaleString("zh-CN", { hour12: false })} 被退回：{d.go_live_rejection.reason}</div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 text-[14px] font-semibold text-slate-900">密钥与上线</div>
        <table className="w-full text-[13px]">
          <thead className="text-left text-[12px] text-slate-500"><tr><th className="py-1">环境</th><th>密钥</th><th>状态</th><th>最近使用</th><th></th></tr></thead>
          <tbody>
            {d.keys.map((k) => (
              <tr key={k.id} className="border-t border-slate-100">
                <td className="py-2">{k.env === "test" ? "测试" : "正式"}</td>
                <td className="font-mono text-[12px]">{k.prefix}…{k.last4}</td>
                <td><Pill tone={k.status === "active" ? "success" : "muted"}>{k.status === "active" ? "有效" : "已吊销"}</Pill></td>
                <td>{ago(k.last_used_at)}</td>
                <td className="text-right">{k.status === "active" && <Can perm="channel_partners.partners.status_manage"><button type="button" onClick={() => revoke(k)} data-testid={`oa-revoke-${k.id}`} className="rounded-lg border border-rose-300 px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50">吊销</button></Can>}</td>
              </tr>
            ))}
            {!liveKey && (
              <tr className="border-t border-slate-100">
                <td className="py-2">正式</td><td>—</td>
                <td><Pill tone="warning">{d.stage === "go_live_requested" ? `对方已申请上线（清单 ${d.checklist_done} / ${d.checklist_total}）` : `等对方申请上线（清单 ${d.checklist_done} / ${d.checklist_total}）`}</Pill></td>
                <td>—</td>
                <td className="text-right"><Can perm="channel_partners.partners.status_manage"><button type="button" disabled={!allDone || d.stage !== "go_live_requested" || issue.isPending} onClick={() => issue.mutate()} data-testid="oa-issue-live" className="rounded-lg bg-blue-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">发正式密钥</button></Can></td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap gap-2">
          <Can perm="channel_partners.partners.status_manage">
            {d.stage === "go_live_requested" && <button type="button" onClick={rejectGoLive} data-testid="oa-reject-golive" className="rounded-lg border border-rose-300 px-3 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50">退回上线申请</button>}
            <button type="button" onClick={() => setReissueAsk(true)} data-testid="oa-reissue-test" className="rounded-lg border border-slate-300 px-3 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50">替对方重发测试密钥</button>
          </Can>
        </div>
        <div className="mt-2 text-[12px] text-slate-400">对方点「申请上线」后铃铛提醒你；「发正式密钥」只有清单全过才能点。吊销立即生效，对方再调接口返回 401，必须填原因。</div>
        {err && <div role="alert" className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{err}</div>}
        {reissued && <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800" data-testid="oa-reissued">测试密钥 <code>{reissued}</code> 已重发，旧的测试密钥立即失效。后台不显示明文：对方登录开放平台「概览 · 密钥」点「领取」取走一次完整密钥（7 天内有效），并会收到通知邮件。</div>}
        {issued && <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800" data-testid="oa-issued-inline">正式密钥 <code className="break-all">{issued}</code> 已发放。后台不显示密钥明文：对方登录开放平台后在「概览 · 密钥」点「领取」取走一次完整密钥（7 天内有效）。</div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-slate-900">近 30 天{stats.isError && <span className="text-[12px] font-normal text-rose-600" data-testid="oa-stats-error">统计加载失败 <button type="button" onClick={() => stats.refetch()} className="underline">重试</button></span>}</div>
        <div className="grid grid-cols-2 gap-3 text-[13px] lg:grid-cols-4">
          {[["调用次数", stats.data?.calls.toLocaleString("en-US")], ["错误率", stats.data ? `${stats.data.error_rate_pct}%` : undefined], ["接口下单", stats.data?.bookings_via_api], ["推送失败未恢复", stats.data?.webhook_failed_unrecovered]].map(([l, v]) => (
            <div key={String(l)}><div className="text-[11px] text-slate-400">{l}</div><div className="text-lg font-semibold text-slate-900">{v ?? "—"}</div></div>
          ))}
        </div>
      </div>
      <PartnerOpenApiOps partnerId={partnerId} />
      {reissueAsk && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" role="dialog" aria-modal="true" data-testid="oa-reissue-confirm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
            <h2 className="mb-2 text-base font-semibold text-slate-900">替对方重发测试密钥</h2>
            <p className="mb-3 text-[13px] text-slate-600">对方现有的测试密钥会<b>立即失效</b>（用旧密钥的程序返回 401）。新密钥后台看不到明文——对方要登录开放平台「概览 · 密钥」点「领取」（7 天内有效），系统会邮件通知对方。确认重发吗？</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setReissueAsk(false)} className="rounded-lg border border-slate-300 px-4 py-1.5 text-[13px] font-medium text-slate-600">取消</button>
              <button type="button" disabled={reissue.isPending} onClick={() => reissue.mutate()} data-testid="oa-reissue-go" className="rounded-lg bg-blue-600 px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40">确认重发</button>
            </div>
          </div>
        </div>
      )}
      {reasonDialog}
    </div>
  );
}
